import { AppHomeButton } from "@pedagogue/ui-core";
import LogoImg from "../../../images/LogoImg";
import React from "react";
import { Typography, Loading, Button } from "../../../components/common";
import CommonAuthLayout from "../CommonAuthLayout";
import { useVerifyHook } from "./useVerifyHook";
import JobTitleModal from "../../../components/Auth/JobTitleModal";
import { CheckCircle, XCircle, KeyRound } from "lucide-react";
import { OtpDeliveryNotice } from "../../../components/common/OtpDeliveryNotice";

const APP_HOME_URL = process.env.REACT_APP_APP_HOME_URL || "https://app.pedagogue.io";

export const Verify: React.FC = () => {
  const {
    status,
    errorMessage,
    currentUser,
    isLoadingUser,
    onJobTitleSubmitted,
    navigateToLogin,
    otpEmail,
    setOtpEmail,
    otpCode,
    setOtpCode,
    otpError,
    isOtpVerifying,
    handleOtpVerify,
  } = useVerifyHook();

  // Show job title modal for legacy users
  if (status === "needs_job_title") {
    return (
      <JobTitleModal
        open={true}
        onSubmit={onJobTitleSubmitted}
        userName={currentUser?.name || currentUser?.full_name || ""}
      />
    );
  }

  // OTP code entry fallback (no tokens/code in URL, no existing session)
  if (status === "otp_entry") {
    return (
      <CommonAuthLayout isLogin={false} onNavigate={navigateToLogin}>
        <div className="md:max-h-[670px] max-w-[464px] mx-auto sm:p-6 pt-2 bg-white rounded-lg flex flex-col gap-4 items-center justify-center">
          <div className="flex w-full items-center gap-2">
            <LogoImg className="md:hidden" />
            <AppHomeButton url={APP_HOME_URL} />
          </div>
          <div className="p-4 bg-primary/10 rounded-full">
            <KeyRound className="text-primary" size={48} />
          </div>
          <Typography variant="h4" weight="bold" className="text-textPrimary">
            Enter your verification code
          </Typography>
          <Typography variant="p" className="text-textSecondary text-center w-full">
            If this email can sign in, we’ll send a 6-digit code to:
            <span className="my-2 block max-w-full break-all font-medium" data-testid="verification-email">{otpEmail}</span>
            Enter it below.
          </Typography>

          <OtpDeliveryNotice />

          <form
            onSubmit={(e) => { e.preventDefault(); handleOtpVerify(); }}
            className="w-full space-y-4 mt-2"
          >
            <div>
              <label
                htmlFor="verify-email"
                className="block text-sm font-medium text-textPrimary mb-1"
              >
                Email Address
              </label>
              <input
                id="verify-email"
                type="email"
                placeholder="your.email@company.com"
                value={otpEmail}
                onChange={(e) => setOtpEmail(e.target.value)}
                className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label
                htmlFor="verify-otp"
                className="block text-sm font-medium text-textPrimary mb-1"
              >
                Verification Code
              </label>
              <input
                id="verify-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={otpCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtpCode(value);
                }}
                className={`w-full text-center text-2xl font-mono tracking-[0.5em] h-14 rounded-lg border ${
                  otpError
                    ? "border-red-400 focus:ring-red-400"
                    : "border-slate-300 focus:ring-primary"
                } focus:outline-none focus:ring-2 focus:border-transparent`}
              />
              {otpError && (
                <p className="mt-1.5 text-sm text-red-500">{otpError}</p>
              )}
            </div>

            <Button
              label={isOtpVerifying ? "Verifying..." : "Verify Code"}
              variant="filled"
              className="w-full bg-primary text-white font-bold h-12"
              disabled={isOtpVerifying || otpCode.length !== 6 || !otpEmail.trim()}
              buttonType="submit"
            />
          </form>

          <button
            type="button"
            onClick={navigateToLogin}
            className="text-sm text-primary hover:underline mt-2"
          >
            Back to Login
          </button>
        </div>
      </CommonAuthLayout>
    );
  }

  return (
    <CommonAuthLayout isLogin={false} onNavigate={navigateToLogin}>
      <div className="max-h-[670px] max-w-[464px] mx-auto sm:p-6 pt-2 bg-white rounded-lg flex flex-col gap-4 items-center justify-center min-h-[300px]">
        {/* Checking status */}
        {(status === "checking" || (status === "success" && isLoadingUser)) && (
          <>
            <Loading />
            <Typography variant="h4" weight="bold" className="text-textPrimary">
              Verifying your account...
            </Typography>
            <Typography variant="p" className="text-textSecondary text-center">
              Please wait while we verify your account.
            </Typography>
          </>
        )}

        {/* Success status (brief, before redirect) */}
        {status === "success" && !isLoadingUser && (
          <>
            <div className="p-4 bg-green-100 rounded-full">
              <CheckCircle className="text-green-600" size={48} />
            </div>
            <Typography variant="h4" weight="bold" className="text-textPrimary">
              Verification successful!
            </Typography>
            <Typography variant="p" className="text-textSecondary text-center">
              Redirecting you to your dashboard...
            </Typography>
            <Loading />
          </>
        )}

        {/* Error status */}
        {status === "error" && (
          <>
            <div className="p-4 bg-red-100 rounded-full">
              <XCircle className="text-red-600" size={48} />
            </div>
            <Typography variant="h4" weight="bold" className="text-textPrimary">
              Verification failed
            </Typography>
            <Typography variant="p" className="text-textSecondary text-center">
              {errorMessage ||
                "We couldn't verify your account. The verification code may have expired."}
            </Typography>
            <div className="flex gap-4 mt-4">
              <Button
                label="Back to Login"
                variant="outline"
                onClick={navigateToLogin}
              />
              <Button
                label="Request New Code"
                variant="filled"
                className="bg-primary text-white"
                onClick={() => {
                  navigateToLogin();
                }}
              />
            </div>
          </>
        )}
      </div>
    </CommonAuthLayout>
  );
};

export default Verify;
